import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";
import { uploadUrl, fmtSize } from "../../lib/format";
import { Pencil, Camera, Trash2, ImagePlus, Images } from "lucide-react";
import PortfolioEditDialog from "./PortfolioEditDialog";
import { SortableArea, useSortableItem, DragHandle } from "./Sortable";
import { act } from "../../lib/useFetch";

const MAX_FILES = 10;

function PortfolioCard({ p, onEdit, onReplace, onDelete }) {
  const { setNodeRef, style, handleProps } = useSortableItem(p.id);
  return (
    <div ref={setNodeRef} style={style} className="glass rounded-2xl overflow-hidden" data-testid={`portfolio-item-${p.id}`}>
      <div className="aspect-[16/10] bg-black/40 relative">
        <img src={uploadUrl(p.image_url)} alt={p.title_ru} className="w-full h-full object-cover" draggable={false} />
        <div className="absolute top-2 left-2 glass rounded-lg"><DragHandle handleProps={handleProps} testid={`portfolio-drag-${p.id}`} /></div>
      </div>
      <div className="p-4">
        <div className="font-medium truncate">{p.title_ru || "Без названия"}</div>
        <div className="text-xs text-gray-500 truncate">{p.title_en} · {p.title_cn}</div>
        <div className="flex items-center justify-between mt-2 text-xs">
          <span className="text-gold font-mono">{p.metric || "—"}</span>
          <span className="text-gray-500 font-mono" data-testid={`portfolio-size-${p.id}`}>{fmtSize(p.size_bytes)}</span>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={() => onEdit(p)} className="btn-outline !py-1.5 !px-3" title="Редактировать" data-testid={`portfolio-edit-${p.id}`}><Pencil size={14} /></button>
          <button onClick={() => onReplace(p)} className="btn-outline !py-1.5 !px-3" title="Заменить фото" data-testid={`portfolio-replace-${p.id}`}><Camera size={14} /></button>
          <button onClick={() => onDelete(p)} className="btn-outline !py-1.5 !px-3" title="Удалить" data-testid={`portfolio-delete-${p.id}`}><Trash2 size={14} /></button>
        </div>
      </div>
    </div>
  );
}

export default function PortfolioPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const singleRef = useRef(null);
  const bulkRef = useRef(null);
  const replaceRef = useRef(null);
  const replaceTarget = useRef(null);

  const load = async () => {
    try { const { data } = await api.get("/portfolio"); setItems(data.portfolio); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const reorder = async (next) => {
    setItems(next);
    await act(api.post("/admin/portfolio/reorder", { ids: next.map((p) => p.id) }), "Порядок сохранён");
    load();
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > MAX_FILES) { toast.error(`Не больше ${MAX_FILES} файлов за раз`); return; }
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/portfolio", fd);
      toast.success(`Загружено: ${data.items.length}`);
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const replace = async (fileList) => {
    const file = fileList?.[0];
    const target = replaceTarget.current;
    if (!file || !target) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { await api.post(`/admin/portfolio/${target.id}/image`, fd); toast.success("Фото заменено"); load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Удалить «${p.title_ru || "работу"}»?`)) return;
    try { await api.delete(`/admin/portfolio/${p.id}`); toast.success("Удалено"); load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const pick = (ref) => { ref.current.value = ""; ref.current.click(); };

  return (
    <div className="glass rounded-2xl p-6" data-testid="panel-portfolio">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h3 className="font-heading text-2xl font-bold">📁 Портфолио ({items.length})</h3>
        <div className="flex gap-2">
          <button disabled={busy} onClick={() => pick(singleRef)} className="btn-outline !py-2 inline-flex items-center gap-2 text-sm" data-testid="portfolio-add-btn"><ImagePlus size={14} /> Добавить фото</button>
          <button disabled={busy} onClick={() => pick(bulkRef)} className="btn-gold !py-2 inline-flex items-center gap-2 text-sm" data-testid="portfolio-bulk-btn"><Images size={14} /> Массовая загрузка</button>
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-6">До {MAX_FILES} фото за раз. Файлы больше 5 МБ автоматически сжимаются. Порядок — перетаскиванием за значок слева сверху.{busy && <span className="text-cyan ml-2" data-testid="portfolio-uploading">Загрузка и сжатие…</span>}</p>
      <input ref={singleRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="portfolio-single-input" />
      <input ref={bulkRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="portfolio-bulk-input" />
      <input ref={replaceRef} type="file" accept="image/*" className="hidden" onChange={(e) => replace(e.target.files)} data-testid="portfolio-replace-input" />
      {items.length === 0 ? <p className="text-gray-400">Работ пока нет.</p> : (
        <SortableArea items={items} onReorder={reorder} grid>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <PortfolioCard key={p.id} p={p} onEdit={setEditing} onDelete={del}
              onReplace={(it) => { replaceTarget.current = it; pick(replaceRef); }} />
          ))}
        </div>
        </SortableArea>
      )}
      {editing && <PortfolioEditDialog item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
